import { useEffect, useMemo, useState } from "react"
import { Store, Plus, CheckCircle, XCircle, Eye, Pencil, Sparkles } from "lucide-react"
import AddStoreModal from "./AddStoreModal"
import EditStoreCategoryModal from "./EditStoreCategoryModal"
import { allStoresData, userData } from "../../store/actions"
import { useDispatch, useSelector } from "react-redux"
import SubscriptionModal from "../../components/subscrptions/SubscriptionModal"
import { getCurrentPlan, hasFeature } from "../../utils/permissions"
import { runSubscriptionUpgrade } from "../../utils/subscriptionUpgrade"
import { resolveStoreCategory } from "../../utils/storeCategory"
import { getStoreCategoryLabel, getStoreCategoryTone } from "../../utils/storeOptions"
import { AnimatePresence } from "framer-motion"

export default function Stores() {
  const userdata = useSelector((state: any) => state.UserReducer.userdata)
  const allstoresdata = useSelector((state: any) => state.UserReducer.allstoresdata || [])
  const user = userdata?.user
  const activeStoreId = String(userdata?.store?._id || "")
  const currentPlan = getCurrentPlan(userdata)
  const canUseMultiStore = hasFeature(currentPlan, "MULTI_STORE")
  const availablePlans = userdata?.availablePlans
  const currentBillingCycle = userdata?.subscription?.billingCycle || userdata?.user?.subscriptionBillingCycle || "monthly"

  const [stores, setStores] = useState(allstoresdata)
  const [isAddingStore, setIsAddingStore] = useState(false)
  const [storeToEdit, setStoreToEdit] = useState<any | null>(null)
  const [openSubscription, setOpenSubscription] = useState(false)

  const dispatch = useDispatch()

  const toggleStatus = (id: string) => {
    setStores((prev: any) =>
      prev.map((store: any) => (String(store.id || store._id) === String(id) ? { ...store, isActive: !store.isActive } : store))
    )
  }

  useEffect(() => {
    dispatch(allStoresData())
  }, [dispatch])

  useEffect(() => {
    setStores(allstoresdata)
  }, [allstoresdata])

  const activeCount = useMemo(() => stores.filter((s: any) => s.isActive).length, [stores])
  const inactiveCount = useMemo(() => stores.filter((s: any) => !s.isActive).length, [stores])

  return (
    <>
      <div className="space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-600 via-blue-700 to-blue-900 p-6 sm:p-7 text-white shadow-xl">
          <div className="pointer-events-none absolute -top-14 -right-10 h-36 w-36 rounded-full bg-cyan-300/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />

          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                <Sparkles size={12} /> Store Management
              </p>
              <h1 className="mt-3 text-2xl sm:text-3xl font-bold">Boutiques</h1>
              <p className="mt-2 text-sm text-white/85 max-w-2xl">Gestion des boutiques, statuts d'activation et suivi global de votre réseau.</p>
            </div>

            <button
              onClick={() => {
                if (canUseMultiStore) setIsAddingStore((prev) => !prev)
                else setOpenSubscription(true)
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-blue-700 hover:bg-blue-50 transition font-semibold"
            >
              <Plus size={18} />
              {canUseMultiStore ? (isAddingStore ? "Fermer le formulaire" : "Nouvelle boutique") : "Multi-boutiques (Business)"}
            </button>
          </div>
        </section>

        {!canUseMultiStore && (
          <div className="bg-amber-50 text-amber-700 border border-amber-200 rounded-xl p-3 text-sm">
            Votre plan actuel ne permet pas d'ajouter plusieurs boutiques. Passez au plan BUSINESS.
          </div>
        )}

        <AnimatePresence>
          {isAddingStore && canUseMultiStore && (
            <AddStoreModal
              isOpen={isAddingStore}
              onClose={() => setIsAddingStore(false)}
              onAdd={(newStore) => {
                dispatch(allStoresData())
                setStores((prev: any) => [newStore, ...prev])
                setIsAddingStore(false)
              }}
            />
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi label="Total boutiques" value={stores.length} color="from-blue-600 to-cyan-600" ring="ring-blue-100" />
          <Kpi label="Boutiques actives" value={activeCount} color="from-emerald-600 to-green-500" ring="ring-emerald-100" />
          <Kpi label="Boutiques inactives" value={inactiveCount} color="from-rose-600 to-red-500" ring="ring-rose-100" />
        </div>

        <div className="hidden md:block bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">Répertoire des boutiques</p>
            <p className="text-xs text-slate-500 mt-0.5">Consultez les statuts, emplacements et types de chaque boutique.</p>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-6 py-3">Boutique</th>
                <th className="px-6 py-3">Ville</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Statut</th>
                <th className="px-6 py-3">Créée le</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {stores.map((store: any) => {
                const storeId = String(store.id || store._id)
                const isActive = Boolean(store.isActive)
                const category = resolveStoreCategory(store)
                const isCurrentStore = storeId === activeStoreId

                return (
                  <tr key={storeId} className="border-t border-slate-100 hover:bg-slate-50/70 transition">
                    <td className="px-6 py-4">
                      <div className="inline-flex items-center gap-3 font-medium text-slate-800">
                        <span className="h-9 w-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                          <Store size={18} />
                        </span>
                        <div>
                          <p>{store.name}</p>
                          {isCurrentStore && (
                            <p className="mt-0.5 text-xs font-medium text-blue-600">Boutique active</p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-slate-600">{store.city}</td>

                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStoreCategoryTone(category)}`}>
                        {getStoreCategoryLabel(category)}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                          <CheckCircle size={14} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                          <XCircle size={14} /> Inactive
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-slate-500">{store.createdAt}</td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100">
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => setStoreToEdit(store)}
                          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100"
                        >
                          <Pencil size={16} />
                        </button>

                        <button onClick={() => toggleStatus(storeId)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100">
                          {isActive ? <XCircle size={16} className="text-red-500" /> : <CheckCircle size={16} className="text-green-500" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}

              {stores.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-slate-500">
                    Aucune boutique enregistrée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4 md:hidden">
          {stores.map((store: any) => {
            const storeId = String(store.id || store._id)
            const isActive = Boolean(store.isActive)
            const category = resolveStoreCategory(store)
            const isCurrentStore = storeId === activeStoreId

            return (
              <div key={storeId} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-3 flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-slate-800">{store.name}</p>
                    <p className="text-xs text-slate-500">{store.city}</p>
                    {isCurrentStore && (
                      <p className="mt-1 text-xs font-medium text-blue-600">Boutique active</p>
                    )}
                  </div>

                  {isActive ? (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Active</span>
                  ) : (
                    <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded-full">Inactive</span>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Catégorie</span>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${getStoreCategoryTone(category)}`}>
                      {getStoreCategoryLabel(category)}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Créée le</span>
                    <span className="text-slate-700">{store.createdAt}</span>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                      <Eye size={16} />
                    </button>

                    <button
                      onClick={() => setStoreToEdit(store)}
                      className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
                    >
                      <Pencil size={16} />
                    </button>

                    <button onClick={() => toggleStatus(storeId)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                      {isActive ? <XCircle size={16} className="text-red-500" /> : <CheckCircle size={16} className="text-green-500" />}
                    </button>
                  </div>
                </div>
              </div>
            )
          })}

          {stores.length === 0 && <div className="text-center text-slate-500 py-10">Aucune boutique enregistrée</div>}
        </div>
      </div>
      <EditStoreCategoryModal
        isOpen={Boolean(storeToEdit)}
        store={storeToEdit}
        onClose={() => setStoreToEdit(null)}
        onUpdated={(updatedStore) => {
          setStores((prev: any) =>
            prev.map((store: any) =>
              String(store.id || store._id) === String(updatedStore.id || updatedStore._id)
                ? { ...store, ...updatedStore }
                : store
            )
          )
          dispatch(allStoresData())
          if (String(updatedStore._id || updatedStore.id) === activeStoreId) {
            dispatch(userData())
          }
        }}
      />
      <SubscriptionModal
        isOpen={openSubscription}
        currentPlan={currentPlan}
        currentBillingCycle={currentBillingCycle}
        plans={availablePlans}
        onClose={() => setOpenSubscription(false)}
        onUpgrade={(planKey, billingCycle) =>
          runSubscriptionUpgrade({
            planKey,
            billingCycle,
            currentPlan,
            currentBillingCycle,
            user,
            dispatch,
            onClose: () => setOpenSubscription(false),
          })
        }
      />
    </>
  )
}

function Kpi({
  label,
  value,
  color,
  ring,
}: {
  label: string
  value: number
  color: string
  ring: string
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ${ring}`}>
      <div className="flex items-center gap-4">
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-lg`}>
          <Store size={18} />
        </div>
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
        </div>
      </div>
    </div>
  )
}
